import React, { useState, useEffect, useRef } from 'react';
import { Menu, X } from 'lucide-react';
import { useMagnetic, useMotion } from '@/lib/motion';

const DEFAULT_LINKS = [
  { label: 'ABOUT', href: '#hero' },
  { label: 'EXPERIENCES', href: '#experiences' },
  { label: 'SPEAKERS', href: '#speakers' },
  { label: 'SPONSORS', href: '#sponsors' },
  { label: 'REGISTRATION', href: '#registration' },
  { label: 'HOTEL', href: '#hotel' },
  { label: 'CONTACT', href: '/contact' },
];
const DEFAULT_REGISTER_URL = 'https://circle.swoogo.com/SEI_ConIII';

// Canonical left-to-right order of nav items, matched to the actual
// top-to-bottom order of homepage sections (verified against live DOM
// 2026-05-28). Used by the runtime sort step below to keep nav order in
// sync with page order regardless of how admin has them stored in the
// nav.links override. Items whose href is not in this list (e.g. CONTACT
// at /contact) are placed after all canonical items in their original
// relative order — preserved by a stable sort.
const CANONICAL_NAV_ORDER = ['#hero', '#experiences', '#speakers', '#sponsors', '#registration', '#hotel'];

// Section IDs that should drive the scroll-spy. Must match the IDs of the
// real DOM nodes on the homepage. The order matters only insofar as it
// determines fallback / tie-break priority; the IntersectionObserver itself
// picks the topmost intersecting section.
const SPY_IDS = ['hero', 'experiences', 'speakers', 'sponsors', 'registration', 'hotel', 'faq'];

// Height of the sticky nav. Centralised so click-offset math and the
// IntersectionObserver rootMargin stay in sync.
const NAV_HEIGHT = 72;

export default function Nav({ nav = {}, motion = {} }) {
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const magneticEnabled = motion?.wow?.magnetic_cursor !== false;
  const registerBtnRef = useMagnetic(magneticEnabled);
  // Track whether we're on the homepage so we only run the scroll-spy there
  // (the same effect that would otherwise observe sections that don't exist
  // on /speakers, /gallery, /contact, etc.).
  const isHomeRef = useRef(typeof window !== 'undefined' && window.location.pathname === '/');

  const rawLinks = Array.isArray(nav.links) && nav.links.length > 0 ? nav.links : DEFAULT_LINKS;
  // Sanitise admin overrides for the two known broken-by-default cases per
  // 2026 nav fix. The filters target ONLY the exact buggy states (duplicate
  // hrefs from the legacy admin seed) and self-disable if admin later sets
  // proper unique hrefs:
  //   • Strip any SCHEDULE link that still points at #speakers (no schedule
  //     section exists yet). If admin later adds a real schedule with a
  //     unique href (e.g. #schedule), it will pass through unchanged.
  //   • Rewrite any HOTEL link that still points at #registration to
  //     #hotel — the hotel card now has id="hotel". If admin later sets
  //     HOTEL to a different href, that admin choice wins.
  const links = rawLinks
    .filter((l) => !(String(l && l.label).toUpperCase() === 'SCHEDULE' && l && l.href === '#speakers'))
    .map((l) => (
      l && String(l.label).toUpperCase() === 'HOTEL' && l.href === '#registration'
        ? { ...l, href: '#hotel' }
        : l
    ))
    // Sort to match the canonical top-to-bottom section order on the page.
    // Items whose href is not in CANONICAL_NAV_ORDER (e.g. CONTACT → /contact)
    // are assigned a sort key of CANONICAL_NAV_ORDER.length so they fall after
    // all canonical anchor items. V8's Array.prototype.sort is stable, so any
    // such items retain their original relative order.
    .slice()
    .sort((a, b) => {
      const ia = CANONICAL_NAV_ORDER.indexOf(a && a.href);
      const ib = CANONICAL_NAV_ORDER.indexOf(b && b.href);
      const ka = ia === -1 ? CANONICAL_NAV_ORDER.length : ia;
      const kb = ib === -1 ? CANONICAL_NAV_ORDER.length : ib;
      return ka - kb;
    });
  const registerLabel = nav.register_label || 'Register Now';
  // Resolve the REGISTER NOW URL. Mirrors the v3.7/v3.8 admin-sanitiser
  // pattern: if the admin override is a known placeholder (the seed
  // `https://test.com` value used during development), fall through to
  // the canonical Swoogo URL instead. Any other admin value passes through
  // verbatim — so once admin updates `settings.nav.register_url` to the
  // real URL (or any non-placeholder), this guard becomes a no-op.
  const PLACEHOLDER_REGISTER_URL_PATTERN = /^https?:\/\/(www\.)?test\.com\/?$/i;
  const rawRegisterUrl = nav.register_url;
  const registerUrl = (rawRegisterUrl && !PLACEHOLDER_REGISTER_URL_PATTERN.test(rawRegisterUrl))
    ? rawRegisterUrl
    : DEFAULT_REGISTER_URL;

  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 1024) setOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Scroll-spy: highlight the nav item whose target section is currently
  // in view. Active band defined by rootMargin '-72px 0px -55% 0px':
  //   • top 72px excluded (under the sticky nav)
  //   • bottom 55% of viewport excluded (so a section becomes active only
  //     once its top has crossed under the nav AND it occupies roughly the
  //     top 45% of the remaining viewport — guarantees one active at a time)
  // Falls back to highlighting #hero when scrollY is at the very top, so
  // ABOUT is always lit on first paint.
  useEffect(() => {
    if (!isHomeRef.current) return undefined;
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const elements = SPY_IDS
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (elements.length === 0) return undefined;

    // Initial value: at top of page, hero (→ ABOUT) should be active.
    if (window.scrollY < NAV_HEIGHT) {
      setActiveId('hero');
    }

    // Track per-target intersecting state and pick the topmost one each tick.
    const intersecting = new Map();
    const recomputeActive = () => {
      // Topmost intersecting element wins. If none intersect (e.g. between
      // sections), keep the last active so we don't flicker to null.
      let topId = null;
      let topY = Infinity;
      intersecting.forEach((isInt, id) => {
        if (!isInt) return;
        const el = document.getElementById(id);
        if (!el) return;
        const y = el.getBoundingClientRect().top;
        if (y < topY) { topY = y; topId = id; }
      });
      if (topId) setActiveId(topId);
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        intersecting.set(entry.target.id, entry.isIntersecting);
      });
      recomputeActive();
    }, {
      root: null,
      rootMargin: `-${NAV_HEIGHT}px 0px -55% 0px`,
      threshold: 0,
    });

    elements.forEach((el) => observer.observe(el));

    // Edge case: when the user scrolls all the way to the very top, the
    // IntersectionObserver may not fire if hero is already considered
    // outside the active band (because rootMargin clips its bottom).
    // A lightweight scroll listener pins ABOUT active in the top-of-page
    // zone so the highlight is always defined.
    const onScroll = () => {
      if (window.scrollY < NAV_HEIGHT) setActiveId('hero');
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  // Offset-aware smooth scroll: account for the 72px sticky nav so the
  // section heading isn't hidden beneath it on click navigation. Works on
  // all viewports. Direct hash URL entry (e.g. /#faq) is covered by the
  // CSS `section[id] { scroll-margin-top: 72px; }` rule in index.css.
  // On a subpage a bare '#section' href only mutates the current URL's
  // hash — there is no such element, so the link silently did nothing.
  // The REAL href is corrected to '/#section' at render time, so native
  // middle-click, modifier-click, copy-link, open-in-new-tab, and
  // no-JavaScript navigation all work with no interception. Home scrolls
  // to the section once its async content has rendered.
  const navHref = (href) =>
    href && href.startsWith('#') && !isHomeRef.current ? '/' + href : href;

  const handleAnchorClick = (e, href) => {
    if (!href || !href.startsWith('#')) return;
    // Subpages navigate natively via the corrected href. On the
    // homepage, intercept ONLY an ordinary primary click — modifier and
    // middle clicks keep their native meaning.
    if (!isHomeRef.current) { setOpen(false); return; }
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - NAV_HEIGHT;
      window.scrollTo({ top, behavior: 'smooth' });
    }
    setOpen(false);
  };

  // Build class string for a nav anchor based on whether it's the active
  // one. The underline is an inset box-shadow (zero layout shift) that
  // transitions in/out smoothly. The shadow is always rendered (transparent
  // when inactive) so the element's painted box never changes size.
  const navLinkClass = (href, base) => {
    const targetId = href && href.startsWith('#') ? href.slice(1) : null;
    const isActive = !!(targetId && targetId === activeId);
    return `${base} ${isActive ? 'text-brand-orange' : 'text-ink-primary'} motion-safe:transition-colors motion-safe:duration-200`;
  };

  // Inline style for the desktop underline. Inset box-shadow draws a 2px
  // line at the bottom of the element WITHOUT consuming layout space, so
  // toggling it does not shift any sibling. Transition runs smoothly via
  // box-shadow + color.
  const desktopUnderlineStyle = (href) => {
    const targetId = href && href.startsWith('#') ? href.slice(1) : null;
    const isActive = !!(targetId && targetId === activeId);
    return {
      boxShadow: isActive ? 'inset 0 -2px 0 currentColor' : 'inset 0 -2px 0 transparent',
      transition: 'color 200ms ease, box-shadow 200ms ease',
    };
  };

  return (
    <header className="sticky top-0 z-50 bg-bg-canvas border-b border-border-line" data-testid="site-nav">
      <div className="container-x flex items-center justify-between h-[72px]">
        <a href="/" className="flex items-center gap-3" data-testid="site-logo">
          <img
            src="/logo-black.png"
            alt="SEI CON — Sports Entertainment Innovation"
            className="h-9 w-auto"
            decoding="async"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-6">
          {links.map((l, idx) => (
            <a
              key={`${l.label}-${idx}`}
              href={navHref(l.href)}
              onClick={(e) => handleAnchorClick(e, l.href)}
              className={navLinkClass(
                l.href,
                'font-body font-medium text-[13px] uppercase tracking-wider2 hover:text-brand-orange py-1'
              )}
              style={desktopUnderlineStyle(l.href)}
              data-testid={`nav-${(l.label || '').toLowerCase()}`}
              data-active={l.href && l.href.startsWith('#') && l.href.slice(1) === activeId ? 'true' : undefined}
            >
              {l.label}
            </a>
          ))}
          <a
            ref={registerBtnRef}
            href={registerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary text-[12px] !py-3 !px-5"
            data-testid="nav-register"
          >
            {registerLabel}
          </a>
        </nav>

        <button
          aria-label="Open menu"
          className="lg:hidden p-2 text-ink-primary"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border-line bg-bg-canvas" data-testid="mobile-drawer">
          <div className="container-x py-4 flex flex-col gap-3">
            {links.map((l, idx) => {
              const targetId = l.href && l.href.startsWith('#') ? l.href.slice(1) : null;
              const isActive = !!(targetId && targetId === activeId);
              return (
                <a
                  key={`m-${l.label}-${idx}`}
                  href={navHref(l.href)}
                  onClick={(e) => handleAnchorClick(e, l.href)}
                  className={`font-body font-medium text-sm uppercase tracking-wider2 py-2 border-b border-border-line motion-safe:transition-colors motion-safe:duration-200 ${isActive ? 'text-brand-orange' : 'text-ink-primary'}`}
                  data-testid={`nav-mobile-${(l.label || '').toLowerCase()}`}
                  data-active={isActive ? 'true' : undefined}
                >
                  {l.label}
                </a>
              );
            })}
            <a
              href={registerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary mt-2"
            >
              {registerLabel}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
